import React, { useEffect, useMemo, useRef, useState } from 'react';
import './css/incidentBanner.css';

/**
 * IncidentStatusBanner
 * - Polls the public status summary endpoint (no auth).
 * - Shows a dismissible alert if there's an active incident OR if fetching fails.
 * - Uses localStorage to remember dismissal for the current incident "fingerprint".
 *
 * Endpoint: https://status.happychef.be/api/v1/summary
 */
const STATUS_URL = 'https://status.happychef.be/api/v1/summary';
const STATUS_PAGE = 'https://status.happychef.be/';

const translations = {
  nl: {
    titleIncident: 'Foutmeldingen: Bekijk Onze Status Pagina Alstublieft',
    titleError: 'Statuscontrole mislukt',
    currentStatus: 'Huidige status:',
    viewStatusPage: 'Bekijk statuspagina',
    close: 'Sluiten',
    couldNotFetch: 'We konden de systeemstatus niet ophalen. Raadpleeg de statuspagina voor de laatste info.',
    // Simple labels for easy-to-read output
    problemLabel: 'Probleem',
    statusLabel: 'Status',
    lastUpdateLabel: 'Laatste update',
    affectedLabel: 'Getroffen onderdeel',
    affectedPluralLabel: 'Getroffen onderdelen',
  },
  en: {
    titleIncident: 'System Incident',
    titleError: 'Status Check Failed',
    currentStatus: 'Current status:',
    viewStatusPage: 'View status page',
    close: 'Close',
    couldNotFetch: 'We could not fetch the system status. Please check the status page for the latest info.',
    problemLabel: 'Problem',
    statusLabel: 'Status',
    lastUpdateLabel: 'Last update',
    affectedLabel: 'Affected component',
    affectedPluralLabel: 'Affected components',
  },
  fr: {
    titleIncident: 'Incident Système',
    titleError: 'Échec de la vérification du statut',
    currentStatus: 'Statut actuel :',
    viewStatusPage: 'Voir la page de statut',
    close: 'Fermer',
    couldNotFetch: 'Impossible de récupérer le statut du système. Veuillez consulter la page de statut pour les dernières informations.',
    problemLabel: 'Problème',
    statusLabel: 'Statut',
    lastUpdateLabel: 'Dernière mise à jour',
    affectedLabel: 'Composant impacté',
    affectedPluralLabel: 'Composants impactés',
  },
};

/**
 * Simpler, plain-language impact/status translations.
 * (More comprehensible for non-technical readers.)
 */
const impactPlainI18n = {
  nl: {
    partial_outage: 'Werkt deels',
    major_outage: 'Grote storing',
    minor_outage: 'Kleine storing',
    degraded_performance: 'Langzamer dan normaal',
    investigating: 'We onderzoeken het',
    maintenance: 'Onderhoud',
    operational: 'Alles werkt',
  },
  en: {
    partial_outage: 'Partially working',
    major_outage: 'Major outage',
    minor_outage: 'Minor outage',
    degraded_performance: 'Slower than usual',
    investigating: 'We are investigating',
    maintenance: 'Maintenance',
    operational: 'All systems working',
  },
  fr: {
    partial_outage: 'Fonctionne partiellement',
    major_outage: 'Panne majeure',
    minor_outage: 'Panne mineure',
    degraded_performance: 'Plus lent que d’habitude',
    investigating: 'Nous enquêtons',
    maintenance: 'Maintenance',
    operational: 'Tout fonctionne',
  },
};

// Robustly detect if there is an ongoing incident from various possible shapes.
function detectIncident(data) {
  try {
    // 0) Explicit HappyChef JSON shape
    if (Array.isArray(data?.ongoing_incidents) && data.ongoing_incidents.length > 0) return true;

    // 1) Atlassian Statuspage-like
    if (data?.status?.indicator && String(data.status.indicator).toLowerCase() !== 'none') return true;
    if (Array.isArray(data?.incidents) && data.incidents.some(i => !/resolved|completed/i.test(String(i?.status || '')))) return true;
    if (Array.isArray(data?.activeIncidents) && data.activeIncidents.length > 0) return true;
    if (Array.isArray(data?.components) && data.components.some(c => String(c?.status || '').toLowerCase() !== 'operational')) return true;

    // 2) Better Stack / custom styles
    if (typeof data?.isOperational === 'boolean' && data.isOperational === false) return true;
    if (typeof data?.operational === 'boolean' && data.operational === false) return true;

    // 3) Summary text heuristics (fallback)
    const sum = String(
      data?.status?.description ||
      data?.summary ||
      data?.page?.status_description ||
      ''
    ).toLowerCase();
    if (sum && !/all systems operational|operational|no incidents/i.test(sum)) {
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

function translateImpact(raw, lang) {
  if (!raw) return '';
  const key = String(raw).toLowerCase();
  const table = impactPlainI18n[lang] || impactPlainI18n.nl;
  return table[key] || raw;
}

/**
 * Build a simple, multi-line status string from the explicit HappyChef shape.
 * Lines (all new lines, very simple words):
 * - "Probleem: {name}"
 * - "Status: {translated impact/status}"
 * - "Laatste update: {message}" (if present)
 * - "Getroffen onderdeel:" / "Getroffen onderdelen:" then one bullet per component: "• {name} – {translated status}"
 */
function buildOngoingIncidentMultiline(data, lang, t) {
  if (!Array.isArray(data?.ongoing_incidents) || data.ongoing_incidents.length === 0) return '';

  const inc = data.ongoing_incidents[0];
  const lines = [];

  // Problem
  const problemName = inc?.name || '-';
  lines.push(`${problemName}`);

  // Status (prefer current_worst_impact, fallback to incident status)
  const impact = inc?.current_worst_impact || inc?.status;
  const statusText = translateImpact(impact, lang) || '-';


  // Components
  const comps = Array.isArray(inc?.affected_components) ? inc.affected_components : [];
  if (comps.length > 0) {
    lines.push(`${comps.length > 1 ? t.affectedPluralLabel : t.affectedLabel}:`);
    comps.forEach(c => {
      const compStatus = translateImpact(c?.current_status, lang);
      lines.push(`• ${c?.name || '-'}${compStatus ? ` – ${compStatus}` : ''}`);
    });
  }

  return lines.join('\n');
}

/** Language-agnostic text for fingerprint stability */
function getStatusCoreText(data) {
  if (Array.isArray(data?.ongoing_incidents) && data.ongoing_incidents.length > 0) {
    const inc = data.ongoing_incidents[0];
    const comps = (inc?.affected_components || []).map(c => c?.name).filter(Boolean).join(',');
    return [
      inc?.name,
      inc?.current_worst_impact || inc?.status,
      inc?.last_update_message,
      comps,
    ].filter(Boolean).join(' | ');
  }
  return (
    data?.status?.description ||
    data?.summary ||
    data?.page?.status_description ||
    data?.message ||
    ''
  );
}

function getStatusText(data, lang, t) {
  // Handle explicit HappyChef shape first, returning simple multi-line string
  if (Array.isArray(data?.ongoing_incidents) && data.ongoing_incidents.length > 0) {
    return buildOngoingIncidentMultiline(data, lang, t);
  }

  // Generic shapes (fallback)
  return (
    data?.status?.description ||
    data?.summary ||
    data?.page?.status_description ||
    data?.message ||
    ''
  );
}

/**
 * Create a "fingerprint" that changes when a different incident appears,
 * so we can remember that the user dismissed the banner for the current situation.
 */
function buildFingerprint(data) {
  try {
    // Explicit HappyChef shape
    const ongoing = (data?.ongoing_incidents || []).map(i => i?.id || i?.name || i?.url);
    const ongoingUpdatedAt = (data?.ongoing_incidents || [])
      .map(i => i?.last_update_at)
      .filter(Boolean)
      .join('|');

    const unresolved = (data?.incidents || [])
      .filter(i => !/resolved|completed/i.test(String(i?.status || '')))
      .map(i => i?.id || i?.name || i?.title || i?.started_at || i?.created_at);

    const updatedAt =
      ongoingUpdatedAt ||
      data?.page?.updated_at ||
      data?.updated_at ||
      data?.last_updated_at ||
      data?.lastUpdated ||
      '';

    const statusDesc =
      getStatusCoreText(data) ||
      data?.summary ||
      '';

    const base = JSON.stringify({ ongoing, unresolved, updatedAt, statusDesc });
    // Simple hash
    let h = 0;
    for (let i = 0; i < base.length; i++) h = (h << 5) - h + base.charCodeAt(i) | 0;
    return String(h);
  } catch {
    return 'unknown';
  }
}

export default function IncidentStatusBanner() {
  const lang = (typeof window !== 'undefined' && localStorage.getItem('preferredLanguage')) || 'nl';
  const t = translations[lang] || translations.nl;

  const [visible, setVisible] = useState(false);
  const [isError, setIsError] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [fingerprint, setFingerprint] = useState('');
  const pollingRef = useRef(null);

  const checkDismissed = useMemo(() => {
    return (fp) => {
      const dismissedFor = (typeof window !== 'undefined') ? localStorage.getItem('incidentBannerDismissedFor') : null;
      return dismissedFor && dismissedFor === fp;
    };
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await fetch(STATUS_URL, { cache: 'no-store', mode: 'cors' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const hasIncident = detectIncident(data);
      const text = getStatusText(data, lang, t) || (hasIncident ? 'Incident detected' : '');
      const fp = buildFingerprint(data);

      setStatusText(text);
      setFingerprint(fp);
      setIsError(false);

      if (hasIncident && !checkDismissed(fp)) {
        setVisible(true);
      } else {
        setVisible(false);
      }
    } catch (err) {
      // Show an error banner that can be dismissed
      setIsError(true);
      setStatusText('');
      setFingerprint('fetch-error'); // single fingerprint for fetch errors
      if (!checkDismissed('fetch-error')) {
        setVisible(true);
      }
    }
  };

  useEffect(() => {
    fetchStatus();

    const setIntervalBasedOnVisibility = () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      const interval = document.hidden ? 180000 : 60000; // 3 min hidden, 1 min visible
      pollingRef.current = setInterval(fetchStatus, interval);
    };

    setIntervalBasedOnVisibility();
    document.addEventListener('visibilitychange', setIntervalBasedOnVisibility);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      document.removeEventListener('visibilitychange', setIntervalBasedOnVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!visible) return null;

  return (
    <div className="incident-banner-component">
      <div
        className={`incident-banner ${isError ? 'incident-error' : 'incident-warning'}`}
        role="alert"
        aria-live="assertive"
      >
        <div className="incident-banner__content">
          <div className="incident-banner__text">
            <strong className="incident-banner__title">
              {isError ? t.titleError : t.titleIncident}
            </strong>
            <span className="incident-banner__status">
              {isError ? t.couldNotFetch : (
                <>
                  
                  {/* Render multi-line, simple status text; CSS uses white-space: pre-line */}
                  <span className="incident-banner__status-text" translate="no">
                    {statusText || '-'}
                  </span>
                </>
              )}
            </span>
          </div>

          <div className="incident-banner__actions">
            <a
              className="incident-banner__button"
              href={STATUS_PAGE}
              target="_blank"
              rel="noreferrer"
            >
              {t.viewStatusPage}
            </a>

            <button
              className="incident-banner__close"
              onClick={() => {
                try {
                  localStorage.setItem('incidentBannerDismissedFor', fingerprint || 'unknown');
                } catch {}
                setVisible(false);
              }}
              aria-label={t.close}
              title={t.close}
            >
              ×
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
